import h from 'hyperscript';

import { logger } from 'cadenza/utils/logging';
import { storeRequestParameterValue } from 'cadenza/api-client/processing-chain-api';
import { on, unByKey } from 'cadenza/utils/event-util';
import { AbstractSelectorResultController } from 'cadenza/selector-result-controllers/abstract-selector-result-controller';
import {
  deleteUrlParamAndDoNotApply,
  getUrl,
  setUrlParamAndDoNotApply
} from 'cadenza/utils/url-param-utils';
import { PARAMETER_PIVOT_CONFIG } from 'cadenza/api-client/api-constants';
import { isValidPivotConfiguration } from 'cadenza/result-table/pivot-table-features/pivot-table-features';
import { SortOrder } from 'cadenza/ui/base-table/sort-order';
import { PIVOT_SORT_CHANGE } from 'cadenza/result-table/pivot-table-features/event-types';
import {
  COLUMN_ORDER_CHANGE,
  COLUMN_VISIBILITY_CHANGE,
  PIVOT_CHANGE,
  TABLE_CONFIG_RESET,
  TABLE_TYPE_CHANGE
} from 'cadenza/result-table/toolbar/table-settings/event-types';

const NAME = 'd-table-controller';

export class TableController extends AbstractSelectorResultController {

  connectedCallback () {
    super.connectedCallback();
    this.classList.add(NAME);
    this._tableSettingsEventKeys = [];
    this._resultTableEventKeys = [];
    this._changedTableSettingsSymbolizer = h('span', '*');
    this._hierarchySelection = document.querySelector('d-hierarchy-selection');
  }

  disconnectedCallback () {
    super.disconnectedCallback();
    this._resetTableSettingsEventKeys();
    this._resetResultTableEventKeys();
    this._resetPivotTableFeaturesEventKeys();
  }

  _resetTableSettingsEventKeys () {
    this._tableSettingsEventKeys = this.resetEventKeys(this._tableSettingsEventKeys);
  }

  _resetResultTableEventKeys () {
    this._resultTableEventKeys = this.resetEventKeys(this._resultTableEventKeys);
  }

  _resetPivotTableFeaturesEventKeys () {
    this._pivotTableFeaturesEventKeys = this.resetEventKeys(this._pivotTableFeaturesEventKeys);
  }

  resetEventKeys (eventKeyArray) {
    if (eventKeyArray && eventKeyArray.length > 0) {
      unByKey(eventKeyArray);
    }
    return [];
  }

  initResultTable (resultTable) {
    if (resultTable) {
      this._setResultTable(resultTable);
    } else {
      this.resetResultTable();
    }
  }

  _setResultTable (resultTable) {
    this._resetResultTableEventKeys();
    this._resultTable = resultTable;
    this._resultTableEventKeys.push(on(resultTable, COLUMN_VISIBILITY_CHANGE, event => this._onTableSettingsChange(event.detail)));
    this._resultTableEventKeys.push(on(resultTable, PIVOT_CHANGE, event => this._onPivotTableSettingsChange(event.detail)));
  }

  resetResultTable () {
    this._resetResultTableEventKeys();
    this._resultTable = null;
  }

  setTableSettings (tableSettings) {
    this._resetTableSettingsEventKeys();
    if (tableSettings) {
      this._tableSettingsEventKeys.push(on(tableSettings, COLUMN_VISIBILITY_CHANGE, event => this._onTableSettingsChange(event.detail)));
      this._tableSettingsEventKeys.push(on(tableSettings, COLUMN_ORDER_CHANGE, event => this._onTableSettingsChange(event.detail)));
      this._tableSettingsEventKeys.push(on(tableSettings, TABLE_CONFIG_RESET, event => this._updateHierarchySelectVisibility(event.detail.tableColumnNamesWithVisibleIndicator)));
      this._tableSettingsEventKeys.push(on(tableSettings, PIVOT_CHANGE, event => this._onPivotTableSettingsChange(event.detail)));
      this._tableSettingsEventKeys.push(on(tableSettings, TABLE_TYPE_CHANGE, event => this._onTableTypeSettingsChange(event.detail)));
    }
    // setTimeout: Be sure, that d-reset-table-settings already has run connectedCallback(), otherwise JS-functions not available
    setTimeout(() => this._setTableSettingsState());
  }

  resetTableSettings () {
    this.resetResultTable();
    this._resetTableSettingsEventKeys();
  }

  setPivotTableFeatures (pivotTableFeatures) {
    this._resetPivotTableFeaturesEventKeys();
    if (pivotTableFeatures) {
      this._pivotTableFeaturesEventKeys.push(
        on(pivotTableFeatures, PIVOT_SORT_CHANGE, (e) => {
          const newPivotConfiguration = {
            ...this._resultTable.pivotConfiguration
          };
          newPivotConfiguration.pivotTableConfig.sortDirections = {
            [e.detail.columnName]: e.detail.order === SortOrder.ASC
          };
          this._onPivotTableSettingsChange(newPivotConfiguration);
        }));
    }
    // setTimeout: Be sure, that d-reset-table-settings already has run connectedCallback(), otherwise JS-functions not available
    setTimeout(() => this._setTableSettingsState());
  }

  resetPivotTableFeatures () {
    this._resetPivotTableFeaturesEventKeys();
  }

  _onTableTypeSettingsChange ({ isPivot, pivotTableConfig }) {
    this._onPivotTableSettingsChange(isPivot ? { pivotTableConfig } : { pivotTableConfig: null });
  }

  _onPivotTableSettingsChange ({ pivotTableConfig }) {
    let urlSearchParams = deleteUrlParamAndDoNotApply({ name: PARAMETER_PIVOT_CONFIG });
    urlSearchParams = deleteUrlParamAndDoNotApply({ name: 'columns' }, urlSearchParams);
    if (pivotTableConfig) {
      this._handlePivotTableConfig(pivotTableConfig, urlSearchParams);
    } else {
      if (this._resultTable) {
        this._resultTable.isInvalidTableConfiguration = false;
      }
      this.updateUrlAndRefreshResults(urlSearchParams);
    }
  }

  _handlePivotTableConfig (pivotTableConfig, urlSearchParams) {
    let isPivotConfigurationValid = false;
    if (this._resultTable) {
      isPivotConfigurationValid = isValidPivotConfiguration(pivotTableConfig, this._resultTable.pivotConfiguration);
      this._resultTable.isInvalidTableConfiguration = !isPivotConfigurationValid;
    }
    this._tableSettingsChanging = true;
    if (isPivotConfigurationValid) {
      this._handlePivotConfigurationValid(pivotTableConfig, urlSearchParams);
    } else {
      deleteUrlParamAndDoNotApply({ name: PARAMETER_PIVOT_CONFIG }, urlSearchParams);
      this.updateUrlAndRefreshResults(urlSearchParams);
    }
    if (this._hierarchySelection) {
      this._hierarchySelection.updateHierarchySelectVisibility(hierarchyName => [ ...pivotTableConfig.rows, ...pivotTableConfig.cols ].includes(hierarchyName));
    }
  }

  _handlePivotConfigurationValid (pivotTableConfig, urlSearchParams) {
    storeRequestParameterValue(pivotTableConfig)
      .then(pivotUUID => {
        urlSearchParams = setUrlParamAndDoNotApply({
          paramName: PARAMETER_PIVOT_CONFIG,
          paramValue: pivotUUID
        }, urlSearchParams);
        return urlSearchParams;
      })
      .then(urlSearchParamsForResults => this.updateUrlAndRefreshResults(urlSearchParamsForResults))
      .catch(err => {
        logger.warn(`error while storing pivot settings in backend: ${err}`);
        this.updateUrlAndRefreshResults(urlSearchParams);
      });
  }

  _onTableSettingsChange ({ tableColumnNamesWithVisibleIndicator }) {
    let urlSearchParams = deleteUrlParamAndDoNotApply({ name: PARAMETER_PIVOT_CONFIG });
    urlSearchParams = deleteUrlParamAndDoNotApply({ name: 'columns' }, urlSearchParams);
    const anyColumnVisible = tableColumnNamesWithVisibleIndicator.some(column => column.visible);
    if (tableColumnNamesWithVisibleIndicator) {
      this._tableSettingsChanging = true;
      this._updateHierarchySelectVisibility(tableColumnNamesWithVisibleIndicator);
      if (anyColumnVisible) {
        this._handleHierarchySelectionChange(tableColumnNamesWithVisibleIndicator, urlSearchParams);
      }
    } else if (anyColumnVisible) {
      this.updateUrlAndRefreshResults(urlSearchParams);
    }
    if (anyColumnVisible) {
      this._resultTable.isInvalidTableConfiguration = false;
    } else {
      this.updateUrlAndRefreshResults(urlSearchParams);
      this._resultTable.hideProgressSpinner();
      this._resultTable.isInvalidTableConfiguration = true;
    }
  }

  _updateHierarchySelectVisibility (tableColumnNamesWithVisibleIndicator) {
    const hierarchySelections = document.querySelectorAll('d-hierarchy-selection');
    hierarchySelections.forEach(hierarchySelection => {
      hierarchySelection.updateHierarchySelectVisibility(hierarchyName => tableColumnNamesWithVisibleIndicator.find(({ name }) => name === hierarchyName)?.visible);
    });
  }

  _handleHierarchySelectionChange (tableColumnNamesWithVisibleIndicator, urlSearchParams) {
    storeRequestParameterValue(tableColumnNamesWithVisibleIndicator)
      .then(columnUUID => {
        return setUrlParamAndDoNotApply({
          paramName: 'columns',
          paramValue: columnUUID
        }, urlSearchParams);
      })
      .then(urlSearchParamsForResults => this.updateUrlAndRefreshResults(urlSearchParamsForResults))
      .catch(err => {
        logger.warn(`error while storing column settings in backend: ${err}`);
        this.updateUrlAndRefreshResults(urlSearchParams);
      });
  }

  _isTableSettingsChangedComparedToInitialState () {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const isColumnsOrPivotManuallyChanged = !!urlSearchParams.get('columns') || !!urlSearchParams.get(PARAMETER_PIVOT_CONFIG);
    const isPersistentPivotConfigChangedToInvalid = this._resultTable?.isInvalidTableConfiguration
      && !isValidPivotConfiguration(this._resultTable?.pivotConfiguration?.pivotTableConfig, this._resultTable?.pivotConfiguration);
    const isOlapConfigChangedToInvalid = !isColumnsOrPivotManuallyChanged
      && !this._resultTable?.pivotConfiguration?.pivotTableConfig
      && this._resultTable?.isInvalidTableConfiguration;
    return isColumnsOrPivotManuallyChanged || isPersistentPivotConfigChangedToInvalid || isOlapConfigChangedToInvalid;
  }

  tableSettingsReset () {
    this._resultTable.isInvalidTableConfiguration = false;
    let urlSearchParams = deleteUrlParamAndDoNotApply({ name: 'columns' });
    urlSearchParams = deleteUrlParamAndDoNotApply({ name: PARAMETER_PIVOT_CONFIG }, urlSearchParams);
    this.updateUrlAndRefreshResults(urlSearchParams);
  }

  updateUrlAndRefreshResults (urlSearchParams) {
    if (!this._resultTable) {
      super.updateUrlAndRefreshResults(urlSearchParams);
    }

    const url = getUrl(urlSearchParams);
    window.history.replaceState(null, null, url);

    this._resultTable.updateTableConfiguration()
      .catch((e) => logger.error('Failed to update table configuration', e))
      .finally(() => {
        this._tableSettingsChanging = false;
        this._setTableSettingsState();
      });
  }

  _setTableSettingsState () {
    const changed = this._isTableSettingsChangedComparedToInitialState();
    this._setResetTableSettingsButtonState(changed);
    this._setChangedColumnConfigurationSymbolizer(changed);
  }

  _setResetTableSettingsButtonState (changed) {
    const resetTableSettings = document.querySelector('.d-table-settings .d-sidebar-reset');
    if (resetTableSettings) {
      resetTableSettings.disabled = !changed;
    }
  }

  _setChangedColumnConfigurationSymbolizer (changed) {
    const pageTitleText = document.querySelector('.d-page-title span');
    if (!pageTitleText) {
      return; // Currently there is no title when displayed inside of a dashlet
    }
    const changedColumnConfigurationSymbolizerAvailable = pageTitleText && pageTitleText.contains(this._changedTableSettingsSymbolizer);
    if (changed && !changedColumnConfigurationSymbolizerAvailable) {
      pageTitleText.append(this._changedTableSettingsSymbolizer);
    } else if (!changed && changedColumnConfigurationSymbolizerAvailable) {
      pageTitleText.removeChild(this._changedTableSettingsSymbolizer);
    }
  }

  isTableSettingsChanging () {
    return !!this._tableSettingsChanging;
  }

}

customElements.define(NAME, TableController);
